import styled from 'styled-components';
import { CardContent } from '@mui/material';
import { Chat, Warning } from '@mui/icons-material';

// Base card component to reduce repetition
const BaseCard = styled.div`
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: 2rem;

    @media (max-width: 768px) {
        padding: 1rem;
        margin-bottom: 1.5rem;
    }
`;

// Base button component for support buttons
const BaseSupportButton = styled.div<{ $expanded: boolean; $variant: 'general' | 'emergency' }>`
    display: flex;
    align-items: center;
    text-align: center;
    padding: 1rem;
    box-sizing: border-box;
    background: white;
    border: 2px solid ${props => {
        if (props.$expanded) {
            return props.$variant === 'general' ? '#1976d2' : '#d32f2f';
        }
        return '#e0e0e0';
    }};
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 0.5rem;
    box-shadow: ${props => {
        if (props.$expanded) {
            const color = props.$variant === 'general' ? '25, 118, 210' : '211, 47, 47';
            return `0 4px 12px rgba(${color}, 0.15)`;
        }
        return '0 2px 4px rgba(0, 0, 0, 0.05)';
    }};
    flex: 1;
    max-width: 100%;
    
    &:hover {
        border-color: ${props => props.$variant === 'general' ? '#1976d2' : '#d32f2f'};
        background: ${props => props.$variant === 'general' ? '#f8f9ff' : '#fff5f5'};
        transform: translateY(-1px);
        box-shadow: ${props => {
            const color = props.$variant === 'general' ? '25, 118, 210' : '211, 47, 47';
            return `0 4px 12px rgba(${color}, 0.15)`;
        }};
    }
    
    &:active {
        transform: translateY(0);
    }
    
    @media (max-width: 768px) {
        padding: 1.5rem;
        gap: 1rem;
        touch-action: manipulation;
        flex: none;
        width: 100%;
        max-width: 100%;
        
        &:active {
            background: ${props => props.$variant === 'general' ? '#f0f2ff' : '#ffeaea'};
        }
    }
`;

// Base form input component
const BaseFormInput = styled.input`
    width: 100%;
    padding: 0.875rem 1rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    background: white;
    transition: all 0.2s ease;
    box-sizing: border-box;
    
    &:focus {
        outline: none;
        border-color: #1976d2;
        box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
    }
    
    &::placeholder {
        color: #9e9e9e;
        font-style: italic;
    }
    
    @media (max-width: 768px) {
        padding: 1rem;
        font-size: 16px;
    }
`;

// Base button component
const BaseButton = styled.button`
    color: white;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    font-weight: 500;
    
    &:hover {
        transform: translateY(-1px);
    }
    
    &:active {
        transform: translateY(0);
    }
    
    &:disabled {
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
    }
`;

export const Container = styled.div<{ $withTalentNav?: boolean }>`
    padding: ${({ $withTalentNav }) => ($withTalentNav ? '0 12px 1rem' : '1rem')};
    max-width: 800px;
    margin: 0 auto;
    min-height: ${({ $withTalentNav }) => ($withTalentNav ? 'auto' : '100vh')};
    box-sizing: border-box;
    width: 100%;
`;

export const PageTitle = styled.h1`
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0.25rem 0 0.5rem;
    color: rgba(0, 0, 0, 0.87);
    font-family: var(--fontMain, Poppins), sans-serif;
`;

export const HeaderSubtitle = styled.h2`
    text-align: center;
    color: var(--text-secondary);
    font-size: 1.125rem;
    font-weight: 400;
    margin: 1rem 0 2rem;
    line-height: 1.5;
    
    @media (max-width: 768px) {
        font-size: 1rem;
        margin: 0.75rem 0 1.5rem;
    }
`;

export const VirtualSupportBanner = styled.div`
    background: linear-gradient(135deg, #1e88e5 0%, #0d47a1 100%);
    color: white;
    padding: 0.85rem; /* reduced to make banner more compact */
    border-radius: 12px;
    margin-bottom: 1rem; /* reduce vertical footprint */
    display: flex;
    align-items: center;
    gap: 0.5rem;
    box-shadow: 0 4px 12px rgba(30, 136, 229, 0.2);
    
    @media (max-width: 768px) {
        padding: 0.65rem; /* smaller on mobile as well */
        margin-bottom: 0.75rem;
        flex-direction: column; /* Keep subtitle below */
        text-align: center;
        gap: 0.5rem;
    }
`;

export const BannerText = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
    
    @media (max-width: 768px) {
        /* Keep title and beta inline on mobile */
        flex-direction: row;
        gap: 0.5rem;
    }
`;

export const BannerTitle = styled.h3`
    font-size: 1rem; /* reduced */
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    @media (max-width: 768px) {
        font-size: 0.95rem; /* reduced */
    }
`;

export const BetaBadge = styled.span`
    background: #ffc107;
    color: #000;
    padding: 0.2rem 0.45rem; /* slightly smaller */
    border-radius: 12px;
    font-size: 0.7rem; /* slightly smaller */
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

export const BannerSubtitle = styled.p`
    font-size: 0.85rem; /* reduced */
    margin: 0.25rem 0 0; /* tighter spacing */
    opacity: 0.9;
    
    @media (max-width: 768px) {
        font-size: 0.8rem; /* reduced */
    }
`;

// Row wrapper for icon + title + beta so they stay inline on mobile
export const BannerHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
`;

// Use BaseCard for all card components
export const SelfServiceCard = styled(BaseCard)``;
export const StillNeedHelpCard = styled(BaseCard)``;
export const LegalTechnicalCard = styled(BaseCard)``;

export const SelfServiceTitle = styled.h2`
    text-align: left;
    color: var(--text-primary);
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
    line-height: 1.3;
    
    @media (max-width: 768px) {
        font-size: 1.25rem;
        margin-bottom: 0.375rem;
    }
`;

export const SelfServiceSubtitle = styled.p`
    text-align: left;
    color: var(--text-secondary);
    font-size: 1rem;
    margin: 0 0 1.5rem 0;
    line-height: 1.5;
    
    @media (max-width: 768px) {
        font-size: 0.9rem;
        margin-bottom: 1.25rem;
    }
`;

export const KnowledgeBaseSection = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.1rem;
    margin-top: 1rem;
    
    @media (max-width: 768px) {
        margin-top: 1rem;
    }
`;

export const OrDivider = styled.span`
    color: var(--text-secondary);
    font-size: 0.875rem;
    font-weight: 500;
`;

export const KnowledgeBaseButton = styled.button`
    background: none;
    border: none;
    color: #1976d2;
    text-decoration: underline;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 500;
    
    &:hover {
        color: #1565c0;
    }
    
    @media (max-width: 768px) {
        font-size: 0.8rem;
    }
`;

export const SearchWrapper = styled.div`
    display: flex;
    align-items: center;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 0.5rem;
    gap: 0.5rem;
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0.75rem;
        padding: 0.75rem;
    }
`;

// Keep icon inside input on all devices (especially mobile)
export const SearchInputGroup = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
`;

export const SearchIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-secondary);
    padding: 0.5rem;
    position: absolute;
    left: 10px;
    pointer-events: none;
    
    @media (max-width: 768px) {
        padding: 0.25rem;
    }
`;

export const StyledTextField = styled.input`
    flex: 1;
    border: none;
    background: transparent;
    padding: 0.75rem;
    font-size: 1rem;
    outline: none;
    padding-left: 2.75rem; /* Extra space for search icon on desktop */
    
    &::placeholder {
        color: #9e9e9e;
    }
    
    @media (max-width: 768px) {
        width: 100%;
        padding: 0.875rem;
        padding-left: 2.5rem; /* Ensure icon spacing on mobile */
        font-size: 16px;
    }
`;

export const SearchButton = styled(BaseButton)`
    background: var(--primary-blue);
    padding: 0.75rem 1.5rem;
    border-radius: 6px;
    font-size: 0.875rem;
    white-space: nowrap;
    
    &:hover {
        background: #1565c0;
    }
    
    @media (max-width: 768px) {
        width: 100%;
        padding: 0.875rem 1.5rem;
        font-size: 16px;
    }
`;

export const SearchErrorText = styled.p`
    color: #FB7672;
    font-size: 0.85rem;
    font-weight: 500;
    margin: 0.5rem 0 0 0;
    line-height: 1.4;

    @media (max-width: 768px) {
        font-size: 0.8rem;
    }
`;

export const StyledCardContent = styled(CardContent)`
    padding: 1.5rem !important;
    
    @media (max-width: 768px) {
        padding: 1rem !important;
    }
`;

export const QuestionTypeContainer = styled.div`
    margin-bottom: 1rem;
`;

export const QueryTypeLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding-bottom: 1rem;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 0.5rem;
    
    &:hover {
        border-color: var(--secondary-main);
        background: #f8f9ff;
    }
    
    @media (max-width: 768px) {
        padding: 1.25rem;
        gap: 1rem;
    }
`;

export const ExpandIcon = styled.span<{ expanded: boolean }>`
    margin-left: auto;
    font-size: 1.25rem;
    transition: transform 0.2s ease;
    transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
    color: var(--text-secondary);
    
    @media (max-width: 768px) {
        font-size: 1.5rem;
    }
`;

export const SupportOptionsContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 1rem;
    margin-bottom: 1rem;
    width: 100%;
    box-sizing: border-box;
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0.75rem;
        width: 100%;
    }
`;

export const ExpandedSection = styled.div<{ $expanded: boolean }>`
    max-height: ${props => props.$expanded ? '500px' : '0'};
    overflow: hidden;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    padding-top: ${props => props.$expanded ? '1rem' : '0'};
    border-top: ${props => props.$expanded ? '1px solid #e9ecef' : 'none'};
    margin-top: ${props => props.$expanded ? '1rem' : '0'};
    opacity: ${props => props.$expanded ? '1' : '0'};
    transform: ${props => props.$expanded ? 'translateY(0)' : 'translateY(-10px)'};
    background: ${props => props.$expanded ? '#f8f9fa' : 'transparent'};
    border-radius: ${props => props.$expanded ? '12px' : '0'};
    margin-bottom: ${props => props.$expanded ? '1rem' : '0'};
`;

export const LabelContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
    
    span {
        font-weight: 500;
        color: var(--text-primary);
        font-size: 1rem;
    }
    
    @media (max-width: 768px) {
        gap: 1rem;
        
        span {
            font-size: 1.125rem;
        }
    }
`;

// Use BaseSupportButton for both support buttons
export const GeneralSupportButton = styled(BaseSupportButton).attrs({ $variant: 'general' })``;
export const EmergencySupportButton = styled(BaseSupportButton).attrs({ $variant: 'emergency' })``;

export const SupportOptionDescription = styled.p`
    color: var(--text-secondary);
    font-size: 0.875rem;
    margin: 0 0 1rem 0;
    padding: 0 1.25rem;
    line-height: 1.5;
    
    @media (max-width: 768px) {
        font-size: 1rem;
        padding: 0 1.5rem;
        margin: 0 0 1.25rem 0;
    }
`;

export const TitleContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
`;

export const PopupTitle = styled.h3`
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
`;

export const ModalText = styled.p`
    color: var(--text-secondary);
    font-size: 1rem;
    line-height: 1.5;
    margin: 0 0 1.5rem;
`;

export const SectionBullet = styled.li`
    color: var(--text-secondary);
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
    list-style: none;
    position: relative;
    padding-left: 1.5rem;
    
    &::before {
        content: '•';
        position: absolute;
        left: 0;
        color: var(--text-tertiary);
        font-weight: bold;
    }
    
    @media (max-width: 768px) {
        font-size: 0.8rem;
        margin-bottom: 0.375rem;
        padding-left: 1.25rem;
    }
`;

export const FormSection = styled.div`
    padding: 1.5rem;
    border-radius: 12px;
    margin-top: 1rem;
    
    @media (max-width: 768px) {
        padding: 1.25rem;
        margin-top: 0.75rem;
    }
`;

export const FormField = styled.div`
    margin-bottom: 1.5rem;
    
    &:last-child {
        margin-bottom: 0;
    }
    
    @media (max-width: 768px) {
        margin-bottom: 1.25rem;
    }
`;

export const FormLabel = styled.label`
    display: block;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
    
    @media (max-width: 768px) {
        font-size: 0.9rem;
        margin-bottom: 0.375rem;
    }
`;

// Use BaseFormInput for form inputs
export const FormInput = styled(BaseFormInput)``;

export const FormTextArea = styled.textarea`
    width: 100%;
    padding: 0.875rem 1rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    background: white;
    transition: all 0.2s ease;
    box-sizing: border-box;
    resize: vertical;
    min-height: 120px;
    font-family: inherit;
    
    &:focus {
        outline: none;
        border-color: #1976d2;
        box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
    }
    
    &::placeholder {
        color: #9e9e9e;
        font-style: italic;
    }
    
    @media (max-width: 768px) {
        padding: 1rem;
        font-size: 16px;
        min-height: 100px;
    }
`;

export const SubmitButton = styled(BaseButton)`
    background: var(--primary-blue);
    padding: 1rem 2rem;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    width: 100%;
    margin-top: 0.5rem;
    
    &:hover {
        background: #1565c0;
        box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
    }
    
    &:disabled {
        background: #bdbdbd;
    }
    
    @media (max-width: 768px) {
        padding: 1.125rem 2rem;
        font-size: 16px;
        margin-top: 0.75rem;
    }
`;

export const LegalSection = styled.div`
    margin-bottom: 2rem;

    @media (max-width: 768px) {
        margin-bottom: 1.5rem;
    }
`;

export const TechnicalSection = styled.div`
    margin-bottom: 0;

    @media (max-width: 768px) {
        margin-bottom: 0;
    }
`;

export const SectionTitle = styled.h3`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--text-primary);
    font-size: 1.125rem;
    font-weight: 600;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
        font-size: 1rem;
        margin-bottom: 0.875rem;
        gap: 0.5rem;
    }
`;

export const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    
    @media (max-width: 768px) {
        width: 20px;
        height: 20px;
    }
`;

export const SectionContent = styled.div`
    color: var(--text-secondary);
    font-size: 0.875rem;
    line-height: 1.5;
    
    @media (max-width: 768px) {
        font-size: 0.8rem;
    }
`;

export const SectionLink = styled.a`
    color: #1976d2;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
        text-decoration: underline;
        color: #1565c0;
    }
    
    &:visited {
        color: #1976d2;
    }
`;

export const SectionText = styled.p`
    margin-bottom: 0.75rem;
    line-height: 1.6;
    
    &:last-child {
        margin-bottom: 0;
    }
    
    @media (max-width: 768px) {
        margin-bottom: 0.625rem;
    }
`;

export const ChatIcon = styled(Chat)`
    color: var(--primary-blue);
`;

export const WarningIcon = styled(Warning)`
    color: var(--error-main);
`;